import React, { useEffect, useState } from 'react';
import { FormControl, FormHelperText, List, ListItem, MenuItem, Select, TextField, Typography, Chip, Box, CircularProgress } from '@material-ui/core';
import { Answer, Question, ScoreInfo } from '../Models/Info';
import { baseEndpoint } from '../App';

interface PropertyQuestionsStepProps {
    properScoreInfo: ScoreInfo[];
    propScoreInfoErrors: number[];
    blockLockError: boolean;
    questions: Question[];
    answers: Answer[];
    onAnswerChange: (event: React.ChangeEvent<{ value: unknown }>, questionIndex: number) => void;
    onFactChange: (event: React.ChangeEvent<{ value: unknown }>, questionIndex: number) => void;
    onAdditionalInfoChange: (event: React.ChangeEvent<{ value: unknown }>, questionIndex: number) => void;
    // Add address prop to make the API call
    address?: string;
}

const BLOCK_LOC_DROPDOWN = [
    "23-parslii-report-block-long-corner-e.png", 
    "23-parslii-report-block-long-corner-w.png",
    "23-parslii-report-block-long-mid.png", 
    "23-parslii-report-block-short-corner-n.png",
    "23-parslii-report-block-short-corner-s.png", 
    "23-parslii-report-block-short-mid.png"
];

// Interface for the API response
interface ApiResponse {
    address: string;
    latitude: number;
    longitude: number;
    subwayResult?: {
        questionId: number;
        answerId: number | null;
        stationName: string | null;
        distanceMeters: number;
        estimatedBlocks: number;
        withinOneBlock: boolean;
    };
    streetLocation?: {
        questionId: number;
        answerId: number | null;
        answerText: string;
    };
    trafficClassification?: {
        questionId: number;
        answerText: string;
        source: string;
        count: number;
        location: {
            lat: number;
            lng: number;
        };
        nearestLocation: {
            street: string;
            borough: string;
            distance: number;
        };
    };
    // Add other potential API response fields here
}

export default function PropertyQuestionsStep(props: PropertyQuestionsStepProps) {
    const [apiData, setApiData] = useState<ApiResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch data from the API when address changes
    useEffect(() => {
        if (props.address) {
            fetchApiData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.address]);

    const fetchApiData = async () => {
        if (!props.address) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const encodedAddress = encodeURIComponent(props.address);
            const response = await fetch(baseEndpoint + 'datageneration/generate?address=' + encodedAddress);
            
            if (response.ok) {
                const data: ApiResponse = await response.json();
                setApiData(data);
            } else {
                setError(`API call failed: ${response.status}`);
            }
        } catch (ex) {
            setError('Failed to fetch data');
            setApiData(null);
            console.error('Error fetching API data:', ex);
        } finally {
            setLoading(false);
        }
    };

    // Helper function to get API suggestion for a specific question
    const getApiSuggestion = (questionId: number) => {
        if (!apiData) return null;
        
        // Match API data to questions based on questionId
        if (apiData.subwayResult && apiData.subwayResult.questionId === questionId) {
            const subway = apiData.subwayResult;
            const stationInfo = subway.stationName ? ` (${subway.stationName})` : '';
            
            return {
                type: "subway",
                data: subway,
                display: subway.withinOneBlock 
                    ? `Subway Access: Within 1 block${stationInfo}`
                    : `Subway Access: ${subway.estimatedBlocks > 0 ? `${subway.estimatedBlocks} blocks away${stationInfo}` : 'No nearby stations'}`
            };
        }
        
        // Handle station name display for questionId 11
        if (questionId === 11 && apiData.subwayResult && apiData.subwayResult.stationName) {
            return {
                type: "station",
                data: apiData.subwayResult,
                display: `Nearest Station: ${apiData.subwayResult.stationName}`
            };
        }
        
        // Handle street location for questionId 3
        if (questionId === 3 && apiData.streetLocation) {
            const street = apiData.streetLocation;
            if (street.answerText && street.answerText !== "Unknown") {
                return {
                    type: "street",
                    data: street,
                    display: `Street Location: ${street.answerText}`
                };
            }
        }
        
        // Handle traffic classification for questionId 7
        if (questionId === 2 && apiData.trafficClassification) {
            const traffic = apiData.trafficClassification;
            if (traffic.answerText) {
                return {
                    type: "traffic",
                    data: traffic,
                    display: `Traffic: ${traffic.answerText} (${Math.round(traffic.count)} pedestrians)`
                };
            }
        }
        
        // Add more question type matches here as your API expands
        return null;
    };
    console.log(props.properScoreInfo);
    return (
        <div>
            {/* API Status Display */}
            {loading && (
                <Box display="flex" alignItems="center" marginBottom="16px">
                    <CircularProgress size={16} style={{ marginRight: "8px" }} />
                    <Typography variant="body2" style={{ color: "#6c757d" }}>
                        Generating property insights...
                    </Typography>
                </Box>
            )}
            
            {error && (
                <Box marginBottom="16px">
                    <Chip 
                        label="⚠️ API Error" 
                        size="small" 
                        style={{ 
                            backgroundColor: "#dc3545", 
                            color: "white",
                            fontSize: "10px"
                        }} 
                    />
                    <Typography variant="caption" style={{ 
                        color: "#dc3545",
                        marginLeft: "8px"
                    }}>
                            {error} - You can still fill out the form manually
                    </Typography>
                </Box>
            )}
            
            {apiData && (
                <Box marginBottom="16px">
                    <Chip 
                        label="✅ Data Generated" 
                        size="small" 
                        style={{ 
                            backgroundColor: "#28a745", 
                            color: "white",
                            fontSize: "10px"
                        }} 
                    />
                    <Typography variant="caption" style={{ 
                        color: "#28a745",
                        marginLeft: "8px"
                    }}>
                        AI insights available for {apiData.address}
                    </Typography>
                </Box>
            )}

            <List>
                {props.properScoreInfo.map((q, i) => {
                    const apiSuggestion = getApiSuggestion(q.questionId);
                    
                    return (
                        <ListItem style={{ display: "flex", marginTop: "10px" }} key={i}>
                            <div style={{ width: "33%" }}>
                                <Typography variant="body1">
                                    {q.questionDescription}
                                    {(props.questions.find(a => a.internalId === q.questionId) as Question).required && 
                                        <strong style={{ color: "red" }}>  *</strong>
                                    }
                                </Typography>
                                
                                {/* Inline API Suggestion */}
                                {apiSuggestion && (
                                    <Box marginTop="8px">
                                        <Chip 
                                            label="🤖 AI Suggestion" 
                                            size="small" 
                                            style={{ 
                                                backgroundColor: "#28a745", 
                                                color: "white",
                                                fontSize: "10px",
                                                marginBottom: "4px"
                                            }} 
                                        />
                                        <Typography variant="caption" style={{ 
                                            color: "#6c757d",
                                            fontStyle: "italic",
                                            display: "block"
                                        }}>
                                            {apiSuggestion.display}
                                        </Typography>
                                    </Box>
                                )}
                            </div>
                            
                            <div style={{ width: "30%", marginLeft: "3%", marginTop: "11px" }}>
                                <FormControl style={{ width: "100%" }} error={props.propScoreInfoErrors.includes(i)}>
                                    <Select
                                        labelId="demo-simple-select-label"
                                        id="demo-simple-select"
                                        value={q.answerId == null ? '' : q.answerId}
                                        onChange={(e) => {
                                            props.onAnswerChange(e, i)
                                        }}
                                    >
                                        {props.answers.map((a, j) => {
                                            if (a.questionId === q.questionId) {
                                                return (
                                                    <MenuItem key={j} value={a.internalId}>
                                                        {a.description} ({a.score})
                                                    </MenuItem>
                                                )
                                            }
                                            else {
                                                return null;
                                            }
                                        })}
                                    </Select>
                                    {props.propScoreInfoErrors.includes(i) && 
                                        <FormHelperText>**Required</FormHelperText>
                                    }
                                </FormControl>
                            </div>
                            
                            <div style={{ width: "14%", display: "flex", marginLeft: "2%" }}>
                                <TextField 
                                    label="Fact" 
                                    style={{ width: "100%" }} 
                                    size="small" 
                                    onChange={(e) => {
                                        props.onFactChange(e, i)
                                    }} 
                                    value={q.fact != null ? q.fact : ""}
                                />
                            </div>
                            
                            <div style={{ width: "15%", display: "flex", marginLeft: "2%" }}>
                                {q.questionId !== 3 ? 
                                    <TextField 
                                        label="Additional Info" 
                                        style={{ width: "100%" }} 
                                        size="small" 
                                        onChange={(e) => {
                                            props.onAdditionalInfoChange(e, i)
                                        }} 
                                        value={q.additionalInfo != null ? q.additionalInfo : ""}
                                    /> :
                                    <FormControl error={props.blockLockError === true} style={{ width: "100%" }}>
                                        <Select
                                            value={q.additionalInfo == null ? '' : q.additionalInfo}
                                            onChange={(e) => {
                                                props.onAdditionalInfoChange(e, i)
                                            }}
                                        >
                                            {BLOCK_LOC_DROPDOWN.map((a, j) => {
                                                return (
                                                    <MenuItem key={j} value={a}>{a}</MenuItem>
                                                )
                                            })}
                                        </Select>
                                        {props.blockLockError && <FormHelperText>*Required</FormHelperText>}
                                    </FormControl>
                                }
                            </div>
                        </ListItem>
                    )
                })}
            </List>
        </div>
    );
}
